/**
 * Rate Card Tab (PR #5g) — read-only engine configuration panel.
 *
 * The Commercial PA rating engine runs server-side in `packages/engine`.
 * Base rates, factors, and governance limits are proprietary IP and must
 * never be persisted in or served from the browser.
 *
 * This tab shows the current engine version (from GET /healthz) and
 * explains the deploy-gate policy. Rate changes require a new engine
 * deployment; the `engine.config.write` permission is reserved for the
 * v2 in-app rate editor (tracked in docs/V2-ROADMAP.md).
 *
 * Access: requires `engine.config.read`.
 */

import { useQuery } from '@tanstack/react-query';
import { Settings2 } from 'lucide-react';

import { cortexApi, isApiConfigured } from '@/services/cortexApi';
import { PageHeader } from '@/components/ui/PageHeader';

export const RateCardTab: React.FC = () => {
  const healthQuery = useQuery({
    queryKey: ['health'],
    queryFn: () => cortexApi.health.check(),
    enabled: isApiConfigured(),
    staleTime: 60_000,
  });

  const engineVersion = healthQuery.data?.version ?? '—';

  return (
    <div className="space-y-6">
      <PageHeader title="Rate Engine" />

      {/* Version banner */}
      <div className="flex items-center gap-4 bg-white rounded-lg border border-slate-200 p-6 shadow-sm">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-indigo-50">
          <Settings2 className="h-6 w-6 text-hr-indigo" />
        </div>
        <div>
          <div className="text-micro font-bold text-slate-400 uppercase">
            Deployed Engine Version
          </div>
          <div className="text-2xl font-bold text-hr-indigo mt-0.5">v{engineVersion}</div>
        </div>
      </div>

      {/* Policy card */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-6 space-y-3">
        <h2 className="text-sm font-bold text-amber-900">Rate changes require a deployment</h2>
        <p className="text-xs text-amber-800">
          Base rates, risk-class multipliers, volume-discount tiers, and governance limits are
          managed in the engine package (
          <code className="font-mono text-xs bg-amber-100 px-1 rounded">packages/engine</code>) and
          deployed server-side with each release. This ensures that pricing IP never leaves the
          server and that every quote can be independently reproduced from the engine version
          recorded at bind time.
        </p>
        <p className="text-xs text-amber-800">
          To update the rate tables, edit{' '}
          <code className="font-mono text-xs bg-amber-100 px-1 rounded">
            packages/engine/src/commercial-pa/defaultConfig.ts
          </code>{' '}
          and deploy a new release. The change will be audited via the standard release pipeline.
        </p>
        <p className="text-xs text-amber-800 font-semibold">
          An in-app rate editor backed by{' '}
          <code className="font-mono text-xs bg-amber-100 px-1 rounded">engine.config.write</code>{' '}
          is planned for v2 (see docs/V2-ROADMAP.md).
        </p>
      </div>

      {/* Read-only summary of governance limits */}
      <div className="bg-white rounded-lg border border-slate-200 p-6 shadow-sm space-y-4">
        <h2 className="text-sm font-bold text-slate-800">Governance (current deployment)</h2>
        <p className="text-micro text-slate-500">
          The values below reflect the engine defaults shipped with this deployment. They cannot be
          edited here.
        </p>
        <div className="grid grid-cols-2 gap-4">
          <div className="border border-slate-100 rounded p-3 bg-slate-50">
            <div className="text-micro font-bold text-slate-400 uppercase">Risk Classes</div>
            <div className="mt-2 space-y-1">
              {['Risk 1 (Low)', 'Risk 2 (Medium)', 'Risk 3 (High)', 'Risk 4 (Extreme)'].map((r) => (
                <div key={r} className="text-xs text-slate-700">
                  {r}
                </div>
              ))}
            </div>
          </div>
          <div className="border border-slate-100 rounded p-3 bg-slate-50">
            <div className="text-micro font-bold text-slate-400 uppercase">
              Currencies Supported
            </div>
            <div className="mt-2 space-y-1">
              {[
                'ZAR (South African Rand)',
                'USD (US Dollar)',
                'NAD (Namibian Dollar)',
                'MUR (Mauritian Rupee)',
              ].map((c) => (
                <div key={c} className="text-xs text-slate-700">
                  {c}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
